/**
 * Procedural map generator. Deterministic: the same seed + options always yield
 * a byte-identical GameMap on any platform.
 *
 * Pipeline per tile:
 *   1. height  = fBm(low frequency) shaped by an optional radial island falloff
 *   2. moisture= fBm(independent seed)
 *   3. temp    = latitude gradient − altitude, blended with fBm
 *   4. classify() -> terrain / biome / buildable / moveCost / deposit candidate
 *   5. deposits placed by clustered noise + seeded rng
 */
import { NoiseField, Prng, asSeed, DepositType, clamp } from '@settlers/shared';
import type { Seed } from '@settlers/shared';
import { GameMap, terrainCode, biomeCode, depositCode } from './gamemap.js';
import { classify, SEA_LEVEL } from './biome.js';

export interface MapGenOptions {
  readonly width: number;
  readonly height: number;
  readonly seed: number;
  /** 0 = flat continents, 1 = strong island falloff (water at the edges). */
  readonly islandFalloff?: number;
  /** Base terrain feature size; larger = broader continents. */
  readonly featureScale?: number;
}

const MAX_ELEVATION = 10;

/** Derive a distinct sub-seed so the three noise fields are uncorrelated. */
const subSeed = (seed: number, salt: number): number =>
  (Math.imul(seed ^ salt, 0x9e3779b1) >>> 0) || 1;

export class MapGenerator {
  generate(options: MapGenOptions): GameMap {
    const { width, height } = options;
    const seed = asSeed(options.seed);
    const islandFalloff = options.islandFalloff ?? 0.6;
    const featureScale = options.featureScale ?? 48;

    const map = new GameMap(width, height, seed);
    const b = map.buffers;

    const heightNoise = new NoiseField(subSeed(seed, 0x01));
    const moistureNoise = new NoiseField(subSeed(seed, 0x02));
    const tempNoise = new NoiseField(subSeed(seed, 0x03));
    const depositNoise = new NoiseField(subSeed(seed, 0x04));
    const rng = new Prng(subSeed(seed, 0x05) as Seed);

    const cx = (width - 1) / 2;
    const cy = (height - 1) / 2;
    const maxDist = Math.hypot(cx, cy);

    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        const i = y * width + x;

        // 1. Height with island falloff.
        let h = heightNoise.fbm(x / featureScale, y / featureScale, 5);
        if (islandFalloff > 0) {
          const d = Math.hypot(x - cx, y - cy) / maxDist;
          const falloff = 1 - Math.pow(d, 2.2);
          h = h * (1 - islandFalloff) + h * falloff * islandFalloff;
        }
        h = clamp(h, 0, 1);

        // 2. Moisture.
        const moisture = clamp(moistureNoise.fbm(x / 40, y / 40, 4), 0, 1);

        // 3. Temperature: equator-warm latitude gradient, colder at altitude.
        const latitude = 1 - Math.abs(y / height - 0.5) * 2;
        const temperature = clamp(
          latitude * 0.6 + tempNoise.fbm(x / 60, y / 60, 3) * 0.4 - Math.max(0, h - SEA_LEVEL) * 0.3,
          0,
          1,
        );

        const c = classify(h, moisture, temperature);

        b.height[i] = h;
        b.moisture[i] = moisture;
        b.temperature[i] = temperature;
        b.terrain[i] = terrainCode(c.terrain);
        b.biome[i] = biomeCode(c.biome);
        b.buildable[i] = c.buildable ? 1 : 0;
        b.moveCost[i] = c.moveCost;
        b.elevation[i] =
          h < SEA_LEVEL ? 0 : Math.round(((h - SEA_LEVEL) / (1 - SEA_LEVEL)) * MAX_ELEVATION);

        // 5. Deposits — clustered by noise, finalised by seeded rng.
        const placed = this.placeDeposit(c.depositCandidate, depositNoise, rng, x, y);
        b.depositType[i] = depositCode(placed.type);
        b.depositAmount[i] = placed.amount;
      }
    }

    return map;
  }

  private placeDeposit(
    candidate: DepositType,
    noise: NoiseField,
    rng: Prng,
    x: number,
    y: number,
  ): { type: DepositType; amount: number } {
    if (candidate === DepositType.None) return { type: DepositType.None, amount: 0 };

    const cluster = noise.fbm(x / 12, y / 12, 3);

    switch (candidate) {
      case DepositType.Forest:
        // Forest terrain is densely wooded; place most tiles.
        return rng.chance(0.85)
          ? { type: DepositType.Forest, amount: rng.int(40, 80) }
          : { type: DepositType.None, amount: 0 };
      case DepositType.FishShoal:
        return cluster > 0.62 && rng.chance(0.25)
          ? { type: DepositType.FishShoal, amount: Number.POSITIVE_INFINITY }
          : { type: DepositType.None, amount: 0 };
      case DepositType.StoneRock:
        return cluster > 0.66 && rng.chance(0.4)
          ? { type: DepositType.StoneRock, amount: rng.int(200, 400) }
          : { type: DepositType.None, amount: 0 };
      case DepositType.IronVein:
      case DepositType.CoalSeam:
      case DepositType.GoldVein:
        return cluster > 0.58 && rng.chance(0.5)
          ? { type: candidate, amount: rng.int(250, 600) }
          : { type: DepositType.None, amount: 0 };
    }
  }
}
